/**
 * NotificationBellButton — top-nav integration for the NotificationsService
 * drawer.
 *
 * MV5 Sprint 2.C. Thin local wrapper: fetches through useNotifications and
 * renders bell + badge + drawer. The design system version lives in
 * @hauraki/ui (NotificationBell) — we ship a local copy here because the
 * web-ide's CSS pipeline (CRA) doesn't yet consume tailwind classes from
 * @hauraki/ui at runtime (the package is pre-built as CSS-in-JS; #45).
 * Once the CSS pipeline is unified (Wave 3), this file replaces its render
 * body with `<HaurakiNotificationBell {...bellProps} />`.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';

const BellIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path
      d="M10 3a5 5 0 00-5 5v3.586L3.293 13.293A1 1 0 004 15h12a1 1 0 00.707-1.707L15 11.586V8a5 5 0 00-5-5z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M8 17a2 2 0 004 0"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export const NotificationBellButton: React.FC = () => {
  const { notifications, unreadCount, markAllRead, markRead } = useNotifications();
  const [open, setOpen] = useState(false);

  const badgeLabel =
    unreadCount > 99 ? '99+' : unreadCount > 0 ? String(unreadCount) : null;

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={
          unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'
        }
        aria-expanded={open}
        style={{
          position: 'relative',
          width: 36,
          height: 36,
          borderRadius: 999,
          border: 'none',
          background: 'transparent',
          color: 'inherit',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <BellIcon />
        {badgeLabel !== null && (
          <span
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
              minWidth: 18,
              height: 18,
              padding: '0 4px',
              background: '#6b4fea',
              color: '#fff',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              boxShadow: '0 0 0 2px var(--chrome-bg, #1a1a1a)',
            }}
          >
            {badgeLabel}
          </span>
        )}
      </button>

      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 90,
            }}
          />
          <div
            role="dialog"
            aria-label="Notifications"
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: 8,
              width: 360,
              maxHeight: '70vh',
              background: '#fff',
              color: '#1a1a1a',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
              zIndex: 100,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <strong>Notifications</strong>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => void markAllRead()}
                  style={{
                    border: 'none',
                    background: 'none',
                    color: '#6b4fea',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Mark all read
                </button>
              )}
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {notifications.length === 0 ? (
                <div style={{ padding: 24, color: '#6b6b6b', textAlign: 'center' }}>
                  You're all caught up.
                </div>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {notifications.map((n) => (
                    <li
                      key={n.id}
                      onClick={() => {
                        if (!n.readAt) void markRead(n.id);
                      }}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #f0f0f0',
                        cursor: n.readAt ? 'default' : 'pointer',
                        background: !n.readAt ? '#f8f5ff' : 'transparent',
                      }}
                    >
                      <div style={{ display: 'flex', gap: 8 }}>
                        {!n.readAt && (
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 999,
                              background: '#6b4fea',
                              marginTop: 6,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 14,
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <span>{describeKind(n.kind)}</span>
                            <time
                              style={{ color: '#6b6b6b', fontSize: 12, fontWeight: 400 }}
                              dateTime={n.createdAt}
                            >
                              {formatRelative(new Date(n.createdAt))}
                            </time>
                          </div>
                          {extractPreview(n.payload) && (
                            <div
                              style={{
                                marginTop: 4,
                                color: '#4b4b4b',
                                fontSize: 13,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {extractPreview(n.payload)}
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div
              style={{
                padding: '10px 16px',
                borderTop: '1px solid #e0e0e0',
                textAlign: 'right',
              }}
            >
              <Link
                to="/settings"
                onClick={() => setOpen(false)}
                style={{ color: '#6b4fea', textDecoration: 'none' }}
              >
                Notification preferences →
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

function describeKind(kind: string): string {
  switch (kind) {
    case 'instructor_message': return 'New message';
    case 'grade_posted': return 'Grade posted';
    case 'escalation_resolved': return 'Escalation resolved';
    case 'cohort_announcement': return 'Cohort announcement';
    case 'billing_reminder': return 'Billing reminder';
    case 'email_verification_pending': return 'Verify your email';
    case 'payment_processing': return 'Payment processing';
    case 'payment_failed': return 'Payment failed';
    case 'refund_processed': return 'Refund processed';
    case 'grade_appeal_submitted': return 'New appeal to review';
    case 'grade_appeal_decided': return 'Appeal decision';
    case 'autosave': return 'Autosaved';
    case 'gpu_job_complete': return 'Training finished';
    case 'gpu_job_failed': return 'Training failed';
    default: return kind.replace(/_/g, ' ');
  }
}
function extractPreview(payload: Record<string, unknown>): string | null {
  if (typeof payload.preview === 'string') return payload.preview;
  if (typeof payload.subject === 'string') return payload.subject;
  if (typeof payload.message === 'string') return payload.message;
  return null;
}
function formatRelative(when: Date): string {
  const diffMs = Date.now() - when.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d`;
  return when.toLocaleDateString();
}
